/**
 * Centralized registry for space-related React Query keys.
 *
 * Mirrors the pattern in `@lib/cache/questions` (issue #653: "No raw
 * strings should be used for cache keys"). Adding a new key family is a
 * one-line change here, and consumers get type-checked factories instead
 * of free-form arrays that drift across files.
 *
 * Scope: keys for spaces, space members, space topics, space events,
 * community lists, and community-detail entries. Consumed by BOTH:
 *   - invalidators in `useRealtimeInvalidation` (SSE handlers for
 *     `SPACE_MEMBER_*`, `SPACE_TOPIC_CHANGED`, etc.), AND
 *   - producers like `useEntityBySlugOrId` (community detail),
 *     `useCommunityMembership`, `useSpaceCardJoin`, and `CreateEvent`.
 *
 * `communityDetailKey()` is intentionally a length-2 prefix so a single
 * invalidation hits every variant (slug-keyed, UUID-keyed, with/without
 * invite token), while `communityDetailEntryKey()` writes a specific entry.
 *
 * Every producer in this family is on a factory: `useSpaceMembers` uses
 * `spaceMembersListKey` (P5), `useInfiniteSpaceEvents` uses `spaceEventsKey`,
 * and `useSpaceTopics`/`useManageTopics` use `spaceTopicsKey`. No raw arrays
 * are left to migrate here.
 */

import type { EventFeedView } from '@lib/cache/events';

// ── Members ─────────────────────────────────────────────────────────────────

/**
 * Base prefix for ALL spaceMembers caches across every space. Used when an
 * action could affect the membership of any space (e.g., accepting an
 * invitation may toggle membership in a space the caller doesn't yet
 * track). Prefer the spaceId-scoped `spaceMembersKey(spaceId)` when the
 * affected space is known.
 */
export const spaceMembersBaseKey = () => ['spaceMembers'] as const;

/**
 * Prefix for all status-filtered member lists of a space. Consumers may
 * append a status segment (e.g. `[..., 'all']` or `[..., 'PENDING']`);
 * the prefix invalidation here covers all of them.
 */
export const spaceMembersKey = (spaceId: string) => ['spaceMembers', spaceId] as const;

/** Status- and search-filtered members list for a space (producer key for
 *  useSpaceMembers). 'approved' is the no-status segment because a missing
 *  `status` is approved-only on the server; '' is the no-search segment.
 *  Prefix-matched by both spaceMembersKey(spaceId) and spaceMembersBaseKey(). */
export const spaceMembersListKey = (spaceId: string, status?: string, search?: string) =>
  [...spaceMembersKey(spaceId), status || 'approved', search || ''] as const;

/** Mutation key for the bulk approve of a space's pending requests. */
export const bulkApproveMembersMutationKey = (spaceId: string) =>
  ['bulkApproveMembers', spaceId] as const;

// ── Events ──────────────────────────────────────────────────────────────────

/** Temporal slice of a space's calendar (separate cached lists per bucket). */
export type SpaceEventPov = 'upcoming' | 'past';

/**
 * Community-scoped event list. Producers pass the active `view` (`EventFeedView`:
 * all / saved / going) as a third segment, and an optional `pov` (upcoming / past)
 * as a fourth, so each filter+bucket caches separately; invalidators omit both to
 * prefix-match every variant of a space at once. Typed `EventFeedView`, not
 * `EventListView` — a space feed never caches under the cross-space `'my-calendars'`
 * tab (that has its own `myCalendarsInfiniteKey`).
 *
 * `includeListed` is the calendar that also shows the events the Space lists without
 * hosting (`include_listed=true`). It is a different answer from the same view and pov, so
 * it takes its own slot: a fifth segment `'listed'`, with an omitted view or pov padded
 * (`'all'`, `'any'`) so the shape never lands on a key that has a pov of its own. Without
 * it the three shapes above are unchanged.
 *
 * The Events tab loads `upcoming` eagerly and `past` lazily (on expand) as two
 * independent queries, hence the pov segment. `spaceEventsKey(spaceId)` stays a
 * length-2 prefix that still invalidates every view/pov/listed variant in one call.
 */
export const spaceEventsKey = (
  spaceId: string,
  view?: EventFeedView,
  pov?: SpaceEventPov,
  includeListed?: boolean,
) =>
  includeListed
    ? (['spaceEvents', spaceId, view ?? 'all', pov ?? 'any', 'listed'] as const)
    : pov
      ? (['spaceEvents', spaceId, view ?? 'all', pov] as const)
      : view
        ? (['spaceEvents', spaceId, view] as const)
        : (['spaceEvents', spaceId] as const);

/**
 * Space-page live-event probe (live layer §5.2): one tiny page-1/per_page-1
 * `GET /spaces/{id}/events?pov=upcoming` whose first item — under the server's
 * ordering (not-ended, soonest start first) — is the space's live-or-soonest
 * event. Produced only by `useSpaceLiveEvent` as the cross-space rail's
 * structural-blind-spot fallback (rail truncation, spaces the viewer hasn't
 * joined).
 *
 * Deliberately a standalone root, NOT nested under `spaceEventsKey(spaceId)`:
 * that prefix is cache-PATCHED as a family (`SimpleEventCard`'s approval
 * mutation `setQueriesData` assumes InfiniteData `pages`), so a plain
 * page-shaped entry under it would be corrupted by the patcher. The trade-off
 * is that `spaceEventsKey` invalidations don't reach this key; freshness comes
 * from the live layer's own contract (rail `staleTime` + refetch on
 * mount/focus), matching the rail and recap sources.
 */
export const spaceLiveProbeKey = (spaceId: string) => ['spaceLiveProbe', spaceId] as const;

/**
 * Prefix for every space's live probe. Because the probe root is standalone
 * (see above), the broad `eventsBaseKey()` sweeps that keep the rail/recap
 * honest never reach it — invalidators whose mutation changes what the probe
 * serves (e.g. a going toggle flipping `is_going` on the probed event) must
 * sweep this base explicitly.
 */
export const spaceLiveProbeBaseKey = () => ['spaceLiveProbe'] as const;

/**
 * How many events the current viewer can see in a space (the `pagination.total` of a
 * `per_page: 1` `GET /spaces/{id}/events`), read by the Activation Kit to tell how many of the
 * space's events are hidden from the viewer.
 *
 * `isSignedIn` is in the key because the list is membership-filtered, so the total differs by
 * viewer. `eventCount` (the space's own count, polled with the space) is in the key so a changed
 * count refetches the total instead of being compared against a stale one for the rest of
 * `staleTime`.
 *
 * A standalone root for the same reason as `spaceLiveProbeKey`: it holds a plain number, which
 * the `spaceEventsKey(spaceId)` family's InfiniteData patchers must never see.
 */
export const spaceVisibleEventsTotalKey = (
  spaceId: string | undefined,
  isSignedIn: boolean,
  eventCount: number | undefined,
) => ['spaceVisibleEventsTotal', spaceId, isSignedIn, eventCount] as const;

// ── Topics ──────────────────────────────────────────────────────────────────

/**
 * Topics keyed by either the space slug or UUID. The two consumer hooks
 * (`useSpaceTopics`, `useManageTopics`) accept either form — this factory
 * reflects that ambiguity rather than papering over it. SSE event handlers
 * call this twice (once with slug, once with UUID) when both are
 * available, since either cache shape may be live.
 *
 * Accepts `undefined` because both consumer hooks take an optional identifier
 * and guard the fetch themselves (`enabled: !!spaceIdentifier`). Invalidators
 * must still check before calling: `['spaceTopics', undefined]` partial-matches
 * no live query, so an unguarded call silently sweeps nothing.
 */
export const spaceTopicsKey = (spaceIdentifier: string | undefined) =>
  ['spaceTopics', spaceIdentifier] as const;

/**
 * Prefix for every space's topics, under either identifier shape. `ManageTopics`
 * keys on the URL slug while `CreateQuestion` and `MeetverseTabContent` key on
 * the space UUID, so a mutation holding only one of the two must sweep this
 * base or the other shape keeps serving a renamed or deleted topic for the rest
 * of its `staleTime`.
 */
export const spaceTopicsBaseKey = () => ['spaceTopics'] as const;

// ── Partners (TECH-86) ──────────────────────────────────────────────────────

/**
 * Prefix for every Partners-page listing of every Space. Sweep it when the change cannot be
 * pinned to one Space; prefer `spacePartnersKey(spaceId)` otherwise.
 */
export const spacePartnersBaseKey = () => ['spacePartners'] as const;

/**
 * Prefix for one Space's Partners-page listing, whatever invite token it was read with. Keyed by
 * the Space UUID (never the slug), so a membership sweep that only knows the id still reaches it.
 * Invalidators use this; the producer writes `spacePartnersListKey`.
 */
export const spacePartnersKey = (spaceId: string) => [...spacePartnersBaseKey(), spaceId] as const;

/**
 * One Partners-page listing (`useSpacePartners`, `GET /spaces/{id}/partners?token=`): the
 * producer's exact slot. `token` is the unlisted Space's `?invite=` token or `undefined`, never
 * `null` (normalise at the call site, as `communityDetailEntryKey` asks), so the two spellings of
 * "no token" never split the cache. The response is trimmed per viewer on the server, so it is
 * swept on every change to the viewer's membership (`invalidateSpaceMembershipQueries`).
 */
export const spacePartnersListKey = (spaceId: string | undefined, token: string | undefined) =>
  [...spacePartnersBaseKey(), spaceId, token] as const;

// ── Lists / aggregations ────────────────────────────────────────────────────

/** Membership role for the `/my-joined` role filter (backend#332). Shared with the producer hook. */
export type SpaceRole = 'admin' | 'moderator' | 'member';

/**
 * Prefix for every joined-spaces list, whatever its filters. Invalidate this after a membership
 * change: it prefix-matches each slot `spacesMyJoinedKey` builds.
 */
export const spacesMyJoinedBaseKey = () => ['spaces', 'my-joined'] as const;

/**
 * One joined-spaces list: the producer's exact cache slot, never a prefix (an empty filter
 * object is still a full slot). `includePending` is its own segment because the list the post
 * composer and the share drawer read (approved + pending, held posts 2026-10) and every other
 * caller's list (approved only) are different responses and must never share an entry.
 */
export const spacesMyJoinedKey = ({
  type,
  role,
  includePending = false,
}: {
  type?: string;
  role?: SpaceRole;
  includePending?: boolean;
}) => [...spacesMyJoinedBaseKey(), type, role, includePending] as const;

/**
 * The filters of the joined-spaces list the post composer reads: the member's community Spaces,
 * approved and pending (a pending member may post there, and the post is held until a host
 * approves). Hand it to `useMyJoinedSpaces` to read that list and to `spacesMyJoinedKey` to name
 * its cache slot, so the composer, the Event posting gate in front of it, the Event page's
 * re-read after a Going and the share drawer's "your spaces" (a pending member may share a post
 * there too, TECH-683) all meet on one entry. Named for the composer, its first reader.
 */
export const COMPOSER_JOINED_SPACES = { type: 'community', includePending: true } as const;

/**
 * Prefix for every community-list entry, whatever its search term: it prefix-matches each slot
 * `communitiesListKey` builds, so an invalidation or an optimistic `setQueriesData` aimed at it
 * reaches every search.
 */
export const communitiesKey = () => ['communities'] as const;

/**
 * One community-list entry (`useInfiniteCommunities`, `GET /spaces?type=community`), per search
 * term: the producer's exact cache slot. An absent term and an empty one are the same unsearched
 * list, so both spell `''` and share one entry.
 */
export const communitiesListKey = (search?: string) => [...communitiesKey(), search || ''] as const;

/**
 * A community-list entry one reader keeps to itself (`useInfiniteCommunities` with a `scope`), per
 * search term. The share drawer reads discover under a scope of its own for each open (TECH-683),
 * so an open never starts from pages read before it. Under `communitiesKey()`, so invalidations and
 * optimistic `setQueriesData` writes reach it as they reach the shared list; its object segment
 * can never be prefix-matched by a `communitiesListKey` search term.
 */
export const communitiesScopedListKey = (scope: string, search?: string) =>
  [...communitiesKey(), { scope, search: search || '' }] as const;

// ── Featured spaces ─────────────────────────────────────────────────────────

type FeaturedSpacesListParams = { skip?: number; limit?: number } | undefined;

/**
 * Prefix for ALL featured-spaces caches. Mirrors `featuredEventsBaseKey()`.
 * Invalidate this after a space's featured status is toggled (admin picker
 * Apply, or the in-space quick toggle) so the discovery rail refreshes.
 * Prefix-matches `featuredSpacesListKey()`.
 */
export const featuredSpacesBaseKey = () => ['featured-spaces'] as const;

/**
 * Paginated featured-spaces list. Mirrors `useReadFeaturedSpaces`. Callers
 * that use the single default page shape can invalidate via the base key
 * above; this parameterized factory keeps per-params entries distinct.
 */
export const featuredSpacesListKey = (params?: FeaturedSpacesListParams) =>
  [...featuredSpacesBaseKey(), params] as const;

/**
 * Prefix for the community-detail family. Consumers append a slug or
 * UUID variant; invalidation here is intentionally a prefix.
 *
 * Pair with `communityDetailEntryKey` below for the full single-entry
 * key. Invalidators use the prefix to hit all variants in one call;
 * producers use the entry key to write a specific cached object.
 */
export const communityDetailKey = () => ['community', 'detail'] as const;

/**
 * Full key for a single community-detail cache entry. Used by the producer
 * (`useEntityBySlugOrId` writes via this exact shape) and by tests that
 * seed the cache for the same hook. Keeping producer + test on the same
 * factory prevents silent drift where a future shape change (e.g. adding
 * a fourth segment) updates one side and not the other.
 *
 * `identifier` may be a slug or a UUID — both forms are valid keys; the
 * hook resolves them identically. `token` is `string` when an invite
 * token is present and `undefined` for the no-token case (production
 * normalizes `?? undefined` at the producer in `useEntityBySlugOrId`).
 * The signature tolerates `null` defensively so a stray caller doesn't
 * type-error, but no production call site should pass `null`. React Query
 * uses `Object.is` for key equality, and `null !== undefined`, so mixing
 * the two splits the cache silently. Tests in this file DO pass `null` to
 * pin the factory's pass-through contract; that is a test-only assertion
 * (preventing a future implementation from silently normalizing `null` and
 * `undefined` into each other), not an endorsement of `null` as a real
 * call-site shape.
 */
export const communityDetailEntryKey = (identifier: string, token: string | null | undefined) =>
  [...communityDetailKey(), identifier, token] as const;

// ── External calendars ────────────────────────────────────────────────────

/**
 * Root of every external-calendar list. The `calendar.sync_changed` SSE handler invalidates
 * this prefix: the list is keyed by slug-or-id, so a payload `spaceId` can't name one key.
 */
export const spaceExternalCalendarsBaseKey = () => ['spaceExternalCalendars'] as const;

/**
 * Admin-only external calendar connections for a space, keyed by the space
 * slug-or-id. Produced by `useSpaceExternalCalendars` and invalidated by the
 * connect/toggle/disconnect mutations in the same module — kept here (not
 * inline in the hook) so producer and invalidators share one source of truth.
 */
export const spaceExternalCalendarsKey = (identifier: string) =>
  [...spaceExternalCalendarsBaseKey(), identifier] as const;

/**
 * A Space's review queue of calendar entries held as likely duplicates (admin only), keyed by the
 * space slug-or-id. The infinite list (`useSpaceDuplicatePairs`) sits at this key itself and the
 * count behind the Setup row (`spaceDuplicatePairCountKey`) under it, so one invalidation after an
 * answer refreshes both. Only the list has `InfiniteData`: write to it with the exact key
 * (`setQueryData`), never by prefix.
 */
export const spaceDuplicatePairsKey = (identifier: string) =>
  ['spaceDuplicatePairs', identifier] as const;

/** The number of pending pairs (`pagination.total` of a one-item page), read by the Setup row. */
export const spaceDuplicatePairCountKey = (identifier: string) =>
  [...spaceDuplicatePairsKey(identifier), 'count'] as const;

/** One answer to a pair (`useAnswerDuplicatePair`), so a card can tell its own answer is in flight. */
export const spaceDuplicatePairAnswerMutationKey = (identifier: string) =>
  ['spaceDuplicatePairAnswer', identifier] as const;
